import { useEffect } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../stores/useStores';
import Button from '../../buttons/Button';
import { CoinWithValue } from '../../CoinWithValue/CoinWithValue';
import Crown from '../../SVGIcons/Crown';

import styles from './JackpotPromoCard.module.scss';

/**
 * Lobby promo card for the Crown Jackpot (FE_DESIGN_SPEC §7.4 variant B): crown art, the
 * `CROWN JACKPOT` wordmark, the coin + the top level pot (the biggest prize on the ladder, by
 * association priority), and a state-driven CTA.
 *
 * The CTA opens game-ui's jackpot popup — it does NOT opt the player in. No lobby widget does: the
 * popup is the single opt-in surface and the only caller of POST /api/v1/jackpot/participation.
 * That keeps opt-in logic in one repo instead of duplicated per widget, and it means the player
 * sees the levels, the contribution cost and How-It-Works before joining rather than being enrolled
 * by a single tap on a carousel card. Only the LABEL follows `participate`.
 *
 * Renders nothing unless the jackpot is visible to this player — no skeleton, no empty carousel
 * slot, no error card — so a jackpot outage is invisible in the lobby. All three jackpot widgets
 * read the one shared JackpotStore poll; this component issues no request of its own.
 */
export const JackpotPromoCard = observer(() => {
  const { t } = useTranslation();
  const { coinTypeStore, gameUiStore, jackpotStore } = useStores();

  const { canShowWidget, showOptInCta, topLevelAmountLabel } = jackpotStore;

  // Deduped in the store: several lobby surfaces are one exposure. See `reportWidgetShown`.
  useEffect(() => {
    if (canShowWidget) {
      jackpotStore.reportWidgetShown();
    }
  }, [canShowWidget, jackpotStore]);

  if (!canShowWidget) {
    return null;
  }

  return (
    <div className={styles.card} data-testid="jackpot-promo-card">
      {/*
        TODO(KRA-303 assets): placeholder crown artwork — the Figma crown render is not in the repo.
        Structure (art block above the wordmark) is final; swap the inner node for the real asset.
      */}
      <div aria-hidden="true" className={styles.art} data-testid="jackpot-promo-card-art">
        <span className={styles.artCrown}>
          <Crown />
        </span>
      </div>

      <div className={styles.wordmark}>{t('jackpot.wordmark')}</div>

      <CoinWithValue
        className={styles.amount}
        coinSide="left"
        coinType={coinTypeStore.coinType}
        dataTestId="jackpot-promo-card-amount"
        valueClassName={styles.amountValue}
      >
        {/*
          The rounded 487.2M, matching the category row — the store's label owns the rule
          (formatJackpotAmountRounded: K/M, one decimal, M capped).
        */}
        {topLevelAmountLabel}
      </CoinWithValue>

      {/*
        Label only: green OPT IN while the player is out, the neutral PLAY NOW treatment once they
        are in (or when they may not join at all). Both taps do the same thing — open the popup.
      */}
      <Button
        btnType={showOptInCta ? 'main' : 'purple'}
        classNameWrapper={styles.ctaWrapper}
        dataTestId="jackpot-promo-card-cta"
        handleOnClick={() => gameUiStore.openJackpotDialog({ source: 'cta' })}
      >
        {showOptInCta ? t('jackpot.optIn') : t('jackpot.playNow')}
      </Button>
    </div>
  );
});
